import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription, catchError, firstValueFrom, retry } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AbstractEnrollmentExtendedService } from '../../abstracts/services/onboarding/enrollment-extended.abstract';
import { OnboardingModuleStatuses } from '../../enums/onboarding-module-status.enum';
import { handleHttpError } from '../../interceptors/handle-http-error';
import { IApiResponse } from '../../interfaces/api/api-response.interface';
import { IAppState } from '../../store/common/interfaces/app-state.interface';
import {
  ApplicationReviewStatusIdAction,
  BusinessProfileStateIdAction,
  BusinessProfileStatusIdAction,
  CustomerPortfolioStateIdAction,
  CustomerPortfolioStatusIdAction,
  DashboardStateIdAction,
  FinancialProfileStateIdAction,
  FinancialProfileStatusIdAction,
  MsaSignatureStatusIdAction,
  NowAccountActivationStatusIdAction,
  OnlyFinancialProfileStateIdAction,
} from '../../store/onboarding/actions/enrollment.actions';
import { destructSubscriptionArray } from '../../ui-helpers/functions/destruct-subscription-array';
import {
  applyEnrollmentIdToQueryParams,
  applyUserEmailToQueryParams,
} from '../../ui-helpers/functions/user-context-url.func';
import { KendoNotificationService } from '../common/kendo-notification.service';
import { LoggerService } from '../common/logger.service';

@Injectable({
  providedIn: 'root',
})
export class EnrollmentExtendedService
  extends AbstractEnrollmentExtendedService
  implements OnDestroy
{
  private apiUrl = environment.apiUrl;
  private dashboardControllerSuffix = 'Onboarding/Onboarding/dashboard/';
  subscriptions: Subscription[] = [];
  constructor(
    private httpClient: HttpClient,
    private store: Store<IAppState>,
    private logger: LoggerService,
    private kns: KendoNotificationService,
  ) {
    super();
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  // updates dashboard state id in enrollement extended table
  async updateDashboardStateId(
    dashboardStateId: string,
  ): Promise<IApiResponse | void> {
    if (dashboardStateId === '-1') return new Promise(() => {});
    const body = {
      dashboardStateId,
    };
    this.store.dispatch(DashboardStateIdAction(body));
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}update-dashboard-state-id`;
    return firstValueFrom(
      this.httpClient.post<IApiResponse>(url, body, { params }),
    );
  }

  async updateDashboardStateIdWithEnrollmentId(
    dashboardStateId: string,
    enrollmentId: string,
  ): Promise<IApiResponse | void> {
    if (dashboardStateId === '-1') return new Promise(() => {});
    const body = {
      dashboardStateId,
      enrollmentId,
    };
    this.store.dispatch(DashboardStateIdAction(body));
    return firstValueFrom(
      this.httpClient.post<IApiResponse>(
        `${this.apiUrl}${this.dashboardControllerSuffix}update-dashboard-state-id`,
        body,
      ),
    );
  }

  // updates dashboard and business profile state ids in enrollement extended table
  async updateBusinessProfileStateId(
    businessProfileStateId: string,
    dashboardStateId: string,
  ): Promise<IApiResponse | void> {
    if (dashboardStateId === '-1') return new Promise(() => {});
    const body = {
      businessProfileStateId,
      dashboardStateId,
    };
    this.store.dispatch(BusinessProfileStateIdAction(body));
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}update-business-profile-state-id`;
    return firstValueFrom(
      this.httpClient.post<IApiResponse>(url, body, { params }),
    );
  }

  // updates dashboard and financial profile state ids in enrollement extended table
  async updateFinancialProfileStateId(
    financialProfileStateId: string,
    dashboardStateId: string,
  ): Promise<IApiResponse | void> {
    if (dashboardStateId === '-1') return new Promise(() => {});
    const body = {
      financialProfileStateId,
      dashboardStateId,
    };
    this.store.dispatch(FinancialProfileStateIdAction(body));
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}update-financial-profile-state-id`;
    return firstValueFrom(
      this.httpClient.post<IApiResponse>(url, body, { params }),
    );
  }

  // updates financial profile state id in enrollement extended table
  async updateOnlyFinancialProfileStateId(
    financialProfileStateId: string,
  ): Promise<IApiResponse | void> {
    const body = {
      financialProfileStateId,
    };
    this.store.dispatch(OnlyFinancialProfileStateIdAction(body));
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}update-only-financial-profile-state-id`;
    return firstValueFrom(
      this.httpClient.post<IApiResponse>(url, body, { params }),
    );
  }

  // updates dashboard and customer portfolio state ids in enrollement extended table
  async updateCustomerPortfolioStateId(
    customerPortfolioStateId: string,
    dashboardStateId: string,
  ): Promise<IApiResponse | void> {
    if (dashboardStateId === '-1') return new Promise(() => {});
    const body = {
      customerPortfolioStateId,
      dashboardStateId,
    };
    this.store.dispatch(CustomerPortfolioStateIdAction(body));
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}update-customer-portfolio-state-id`;
    return firstValueFrom(
      this.httpClient.post<IApiResponse>(url, body, { params }).pipe(
        retry(3), // retry a failed request up to 3 times
        catchError((error: HttpErrorResponse) =>
          handleHttpError(error, this.logger, this.kns),
        ), // handle error if all request attempts fail
      ),
    );
  }

  async createEnrollmentExtended(): Promise<IApiResponse> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}enrollment-extended`;
    const result = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url, { params }),
    );
    return result;
  }

  override async getEnrollmentExtended(): Promise<IApiResponse> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    params = applyUserEmailToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}get-enrollment-extended`;
    const result = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url, { params }),
    );
    return result;
  }

  async activateBusinessProfile(): Promise<IApiResponse> {
    this.store.dispatch(
      BusinessProfileStatusIdAction({
        businessProfileStatusId: OnboardingModuleStatuses.ACTIVE,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}activate-business-profile`;
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async completeBusinessProfile(): Promise<IApiResponse> {
    this.store.dispatch(
      BusinessProfileStatusIdAction({
        businessProfileStatusId: OnboardingModuleStatuses.COMPLETED,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}complete-business-profile`;
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async activateFinancialProfile(): Promise<IApiResponse> {
    this.store.dispatch(
      FinancialProfileStatusIdAction({
        financialProfileStatusId: OnboardingModuleStatuses.ACTIVE,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}activate-financial-profile`;
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async completeFiancialProfile(): Promise<IApiResponse> {
    this.store.dispatch(
      FinancialProfileStatusIdAction({
        financialProfileStatusId: OnboardingModuleStatuses.COMPLETED,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}complete-financial-profile`;
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async activateCustomerPortfolio(): Promise<IApiResponse> {
    this.store.dispatch(
      CustomerPortfolioStatusIdAction({
        customerPortfolioStatusId: OnboardingModuleStatuses.ACTIVE,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}activate-customer-portfolio`;
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async completeCustomerPortfolio(): Promise<IApiResponse> {
    this.store.dispatch(
      CustomerPortfolioStatusIdAction({
        customerPortfolioStatusId: OnboardingModuleStatuses.COMPLETED,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}complete-customer-portfolio`;
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async activateApplicationReview(): Promise<IApiResponse> {
    this.store.dispatch(
      ApplicationReviewStatusIdAction({
        applicationReviewStatusId: OnboardingModuleStatuses.ACTIVE,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}activate-application-review`;
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async completeApplicationReview(): Promise<IApiResponse> {
    this.store.dispatch(
      ApplicationReviewStatusIdAction({
        applicationReviewStatusId: OnboardingModuleStatuses.COMPLETED,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}complete-application-review`;
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async activateMsa(): Promise<IApiResponse> {
    this.store.dispatch(
      MsaSignatureStatusIdAction({
        msaSignatureStatusId: OnboardingModuleStatuses.ACTIVE,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}activate-msa`;
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async completeMsa(): Promise<IApiResponse> {
    this.store.dispatch(
      MsaSignatureStatusIdAction({
        msaSignatureStatusId: OnboardingModuleStatuses.COMPLETED,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}complete-msa`;
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async activateNowAccountActivation(): Promise<IApiResponse> {
    this.store.dispatch(
      NowAccountActivationStatusIdAction({
        nowAccountActivationStatusId: OnboardingModuleStatuses.ACTIVE,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}activate-nowaccount-activation`;
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async completeNowAccountActivation(): Promise<IApiResponse> {
    this.store.dispatch(
      NowAccountActivationStatusIdAction({
        nowAccountActivationStatusId: OnboardingModuleStatuses.COMPLETED,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}complete-nowaccount-activation`;
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async activateClient(): Promise<IApiResponse> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.dashboardControllerSuffix}activate-client`;
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }
}
